import { type ReactNode, useReducer } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import {
  ProductListContext,
  flavoursReducer,
  type FlavourToAdd,
} from '../../../../../Context/ProductListContext';

export default function VtsProductsProvider({
  children,
  erpIdToIdMap,
}: {
  children: ReactNode;
  erpIdToIdMap: Map<number, number>;
}) {
  const [flavoursToAdd, dispatch] = useReducer(flavoursReducer, []);

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: (
      positions: {
        product_attribute_id: number;
        quantity: number;
        price: number;
      }[]
    ) => {
      const formData = new FormData();
      formData.append('positionsJson', JSON.stringify(positions));
      return axios.post('/api/onlineshop/vts-basket-add-position', formData);
    },
  });

  const addFlavour = (data: FlavourToAdd) => {
    dispatch({ type: 'ADD', payload: data });
  };

  const removeFlavour = (flavourId: number) => {
    dispatch({
      type: 'REMOVE',
      payload: { flavourId, quantity: 0, price: 0 },
    });
  };

  const getUnits = (flavourIds?: number[]) => {
    let count = 0;
    flavoursToAdd.forEach(({ flavourId, quantity }) => {
      if (flavourIds && !flavourIds.some((id) => id === flavourId)) {
        return;
      }
      count += quantity;
    });
    return count;
  };

  const getTotal = (flavourIds?: number[]) => {
    let count = 0;
    flavoursToAdd.forEach(({ flavourId, price, quantity }) => {
      if (flavourIds && !flavourIds.some((id) => id === flavourId)) {
        return;
      }
      count += quantity * price;
    });
    return count;
  };

  const addToBasket = () => {
    if (flavoursToAdd.length > 0) {
      const positions = flavoursToAdd.map(({ flavourId, quantity, price }) => ({
        product_attribute_id: erpIdToIdMap.get(flavourId) ?? flavourId,
        quantity,
        price,
      }));

      mutate(positions, {
        onSuccess: () => {
          dispatch({ type: 'CLEAR' });
          setTimeout(() => resetMutation(), 2500);
        },
      });
    }
  };

  const clearFlavoursToAdd = () => {
    dispatch({ type: 'CLEAR' });
  };

  return (
    <ProductListContext.Provider
      value={{
        clearFlavoursToAdd,
        flavoursToAdd,
        addFlavour,
        removeFlavour,
        getUnits,
        getTotal,
        addToBasket,
        isPending,
        isSuccess,
      }}
    >
      {children}
    </ProductListContext.Provider>
  );
}
